import React, { useMemo, useState, useEffect, useCallback } from 'react';

import { IconButton } from '@eika/button';
import { IconTypes } from '@eika/icon';

import { FuturePaymentsList, FuturePaymentListSkeleton } from '../../components/FuturePaymentsList';
import { FuturePaymentsFilterModal } from '../../components/FuturePaymentsList/FuturePaymentsFilterModal';
import { ThemedListWrapper } from '../../components/ThemedListWrapper';
import FilterButton from '../../components/transaction/TransactionFilter/SearchResetButtons/FilterButton';
import { useConfig } from '../../defaultConfig';
import { useAccounts } from '../../fetchHooks/useAccount';
import { useAllFuturePayments } from '../../fetchHooks/useFuturePayments';
import { useAllLoanPayments } from '../../fetchHooks/useLoans';
import usePaymentLimits from '../../fetchHooks/usePaymentLimits';
import type { Account } from '../../generated-types';
import useTranslation, { format } from '../../hooks/useTranslation';
import * as FuturePaymentService from '../../services/core/FuturePaymentService';
import formatFuturePayments from '../../utils/mapper/futurepaymentsUtil';
import FuturePaymentsListError from './FuturePaymentsListError';

import './FuturePaymentsListPage.scss';

const FuturePaymentsListPage: React.FC<{ withTitle?: boolean }> = ({ withTitle = false }) => {
    const {
        featureToggles: { enableFakedLoanFuturePayment },
    } = useConfig();
    const translated = useTranslation();
    const [filterButtonRef, setFilterButtonRef] = useState<HTMLButtonElement | null>(null);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [visibleAccounts, setVisibleAccounts] = useState<Account[]>([]);

    const { data: futurePaymentResponse, ...futurePaymentsQuery } = useAllFuturePayments();
    const { data: loanPayments = [], ...loanPaymentsQuery } = useAllLoanPayments(enableFakedLoanFuturePayment);
    const { data: accounts, ...accountQuery } = useAccounts();
    const { data: paymentLimits } = usePaymentLimits();

    const isLoading = futurePaymentsQuery.isLoading || accountQuery.isFetching;
    const error = futurePaymentsQuery.error || accountQuery.error;

    const refresh = useCallback(() => {
        accountQuery.refetch();
        futurePaymentsQuery.refetch();
        loanPaymentsQuery.refetch();
    }, [accountQuery, futurePaymentsQuery, loanPaymentsQuery]);

    useEffect(() => {
        if (accounts) {
            setVisibleAccounts(accounts);
        }
    }, [accounts]);

    const formattedFuturePayments = useMemo(() => {
        const payments = FuturePaymentService.extendWithFakedLoans(futurePaymentResponse || [], loanPayments);
        const actualFuturePayments = payments.filter((futurePayment) =>
            visibleAccounts.some((a) => a.accountNumber.value === futurePayment.from?.accountNumber?.value),
        );

        const total = FuturePaymentService.getTotalResult(actualFuturePayments);

        return formatFuturePayments(actualFuturePayments, undefined, total?.totalAmount, accounts);
    }, [futurePaymentResponse, loanPayments, accounts, visibleAccounts]);

    const numberOfAccountsSelected = useMemo(() => {
        return format(
            visibleAccounts.length === 1
                ? translated.futurePaymentsList.chosenAccount
                : translated.futurePaymentsList.chosenAccounts,
            visibleAccounts.length,
        );
    }, [visibleAccounts, translated]);

    const isError = !!error;
    const listLoading = !isError && !!futurePaymentResponse && accounts;
    return (
        <ThemedListWrapper title={withTitle && <h2>{translated.futurePaymentsList.title}</h2>}>
            {isError && <FuturePaymentsListError error={error} refresh={refresh} />}
            {listLoading && (
                <FuturePaymentsList
                    formattedFuturePayments={formattedFuturePayments}
                    showFromAccount
                    paymentLimits={paymentLimits}
                >
                    {formattedFuturePayments.length === 0 && <div />}
                    <IconButton
                        screenReaderText={translated.futurePaymentsList.ariaLabel}
                        icon={IconTypes.INNSTILLINGER_24}
                        onClick={() => setFilterModalOpen(true)}
                        ref={(ref) => setFilterButtonRef(ref)}
                    />
                    {accounts && visibleAccounts.length !== accounts.length && (
                        <FilterButton
                            icon={IconTypes.KRYSS_24}
                            className="search-reset-button"
                            elementToFocusOnExit={filterButtonRef}
                            onClick={() => setVisibleAccounts(accounts)}
                            aria-label={translated.futurePaymentsList.removeChosenAccounts}
                        >
                            <span>{numberOfAccountsSelected}</span>
                        </FilterButton>
                    )}
                </FuturePaymentsList>
            )}
            {isLoading && (
                <FuturePaymentListSkeleton
                    hideTitle
                    count={futurePaymentResponse?.length ?? formattedFuturePayments.length ?? 5}
                />
            )}

            {!isLoading && accounts && (
                <FuturePaymentsFilterModal
                    open={filterModalOpen}
                    setOpen={setFilterModalOpen}
                    onSubmit={(accounts: Account[]) => {
                        setVisibleAccounts(accounts);
                        setFilterModalOpen(false);
                    }}
                    accounts={accounts}
                />
            )}
        </ThemedListWrapper>
    );
};

export default FuturePaymentsListPage;
